/**
 * DiscountCardBlock Component
 *
 * Custom BlockNote React component for displaying discount cards with company branding,
 * customizable discount badges, and call-to-action buttons.
 *
 * Features:
 * - Company logo upload with 200px height variant processing
 * - Customizable discount badge (text and color)
 * - Rich text details section
 * - External link with CTA button
 * - Two-column desktop layout, single-column mobile
 * - Edit/View mode distinction
 *
 * Props (from block.props):
 * - logoImageUrl: Company logo image URL (legacy)
 * - logoImageBlobId: ActiveStorage blob signed ID (preferred for variants)
 * - title: Product/service name
 * - description: Brief description (up to 500 chars, supports markdown)
 * - details: Rich text deal details (HTML converted from markdown)
 * - linkUrl: External link URL
 * - linkText: CTA button text (default: "View Deal")
 * - badgeText: Discount text (e.g., "70%", "BOGO")
 * - badgeColor: Badge color variant (green/yellow/orange/red)
 *
 * Markdown Support:
 * - Users can type markdown syntax in Description and Deal Details fields
 * - On blur, markdown is converted to HTML for storage and rendering
 * - Supports detection of existing HTML to prevent double-conversion
 * - If props contain HTML (e.g., from SSR), plain text is extracted for editing
 * - Supported syntax: **bold**, *italic*, [link](url), paragraphs, line breaks
 */

import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  sanitizeHtml,
  sanitizeUrl,
  normalizeWebUrl,
} from "../../utils/sanitize";
import { markdownToHtml, htmlToMarkdown } from "../../utils/markdown";
import ImageUploadField from "./shared/ImageUploadField.jsx";

/**
 * Edit Mode Component
 *
 * Provides form fields for editing all discount card properties
 * including image upload, text inputs, and color selection
 */
function DiscountCardEditor({ block, onUpdate }) {
  const [touched, setTouched] = useState({
    logoImageUrl: false,
    title: false,
    description: false,
    details: false,
    linkUrl: false,
    badgeText: false,
  });
  const [collapsed, setCollapsed] = useState(() => {
    const p = block.props || {};
    const hasContent = Boolean(
      (p.title && p.title.trim()) ||
        (p.description && p.description.trim()) ||
        (p.details && p.details.trim()) ||
        (p.linkUrl && p.linkUrl.trim()) ||
        (p.linkText && p.linkText.trim()) ||
        (p.badgeText && p.badgeText.trim()),
    );
    return hasContent;
  });

  // Helper function to detect if a string contains HTML tags
  const containsHtml = (str) => {
    if (!str) return false;
    return /<\/?[a-z][\s\S]*>/i.test(str);
  };

  // Initialize markdown state - if props contain HTML, convert back to markdown
  // This allows users to see and edit the original markdown syntax
  const [descriptionMarkdown, setDescriptionMarkdown] = useState(() => {
    const desc = block.props?.description || "";
    return containsHtml(desc) ? htmlToMarkdown(desc) : desc;
  });

  const [detailsMarkdown, setDetailsMarkdown] = useState(() => {
    const details = block.props?.details || "";
    return containsHtml(details) ? htmlToMarkdown(details) : details;
  });

  // Handle image upload callback
  const handleImageUploaded = (url, variants, blobData) => {
    // Store both the blob signed ID (for SSR variant generation) and URL (for editor preview)
    // The signed ID is preferred as it never expires and allows server-side variant generation
    if (blobData?.signed_id) {
      onUpdate("logoImageBlobId", blobData.signed_id);
    }
    // Use the 200px variant for discount cards, or fall back to original
    const imageUrl = variants?.["200px"] || url;
    onUpdate("logoImageUrl", imageUrl);
  };

  // Handle description markdown change
  const handleDescriptionChange = (event) => {
    setDescriptionMarkdown(event.target.value);
  };

  // Convert description markdown to HTML on blur
  const handleDescriptionBlur = () => {
    setTouched((t) => ({ ...t, description: true }));
    const html = markdownToHtml(descriptionMarkdown);
    onUpdate("description", html);
  };

  // Handle details markdown change
  const handleDetailsChange = (event) => {
    setDetailsMarkdown(event.target.value);
  };

  // Convert details markdown to HTML on blur
  const handleDetailsBlur = () => {
    setTouched((t) => ({ ...t, details: true }));
    const html = markdownToHtml(detailsMarkdown);
    onUpdate("details", html);
  };

  const isBlank = (v) => !(v && String(v).trim());
  const urlInvalid = (v) => {
    const normalized = normalizeWebUrl(v || "");
    const sanitized = sanitizeUrl(normalized);
    return isBlank(v) || sanitized === "#";
  };

  return (
    <div className="discount-card-editor w-full p-4 border border-gray-200 rounded-lg bg-gray-50">
      <span className="sr-only text-lg font-semibold mb-4">
        Discount Card Settings
      </span>

      {/* Logo Upload - Using Shared Component */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Company Logo
        </label>
        <ImageUploadField
          currentImageUrl={block.props?.logoImageUrl}
          onImageUploaded={handleImageUploaded}
          blockType="discountCard"
          blockId={block.id}
          accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/avif"
          maxSize={5 * 1024 * 1024}
          hasError={touched.logoImageUrl && isBlank(block.props?.logoImageUrl)}
          onTouched={() => setTouched((t) => ({ ...t, logoImageUrl: true }))}
        />
      </div>

      {/* Title */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-neutral-700 mb-2">
          Title (required)
        </label>
        <input
          type="text"
          value={block.props?.title || ""}
          onChange={(e) => onUpdate("title", e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, title: true }))}
          maxLength={200}
          className={`form-control${touched.title && isBlank(block.props?.title) ? " error" : ""}`}
          placeholder="Product or service name"
        />
        <div className="mt-2">
          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="text-sm text-blue-600 hover:text-blue-800 inline-flex items-center"
            aria-expanded={!collapsed}
            aria-controls={`discount-card-section-${block.id}`}
          >
            <svg
              className={`w-4 h-4 mr-1 transform transition-transform ${collapsed ? "" : "rotate-90"}`}
              viewBox="0 0 20 20"
              fill="currentColor"
              aria-hidden="true"
            >
              <path fillRule="evenodd" d="M6 6l6 4-6 4V6z" clipRule="evenodd" />
            </svg>
            {collapsed ? "Show details" : "Hide details"}
          </button>
        </div>
      </div>

      {/* Description */}
      {!collapsed && (
        <div id={`discount-card-section-${block.id}`} className="">
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Description (required)
            </label>
            <textarea
              value={descriptionMarkdown}
              onChange={handleDescriptionChange}
              onBlur={handleDescriptionBlur}
              maxLength={500}
              rows={3}
              className={`form-control${touched.description && isBlank(block.props?.description) ? " error" : ""}`}
              placeholder="Brief description of the offer (supports **bold** and *italic*)"
            />
            <span className="text-xs text-neutral-500 mt-1 block">
              {descriptionMarkdown.length}/500 characters · Supports markdown:
              **bold**, *italic*, [link](url)
            </span>
          </div>

          {/* Details */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Deal Details (required)
            </label>
            <textarea
              value={detailsMarkdown}
              onChange={handleDetailsChange}
              onBlur={handleDetailsBlur}
              rows={4}
              className={`form-control${touched.details && isBlank(block.props?.details) ? " error" : ""}`}
              placeholder="Enter deal terms and conditions (supports **bold**, *italic*, and [links](url))"
            />
            <span className="text-xs text-neutral-500 mt-1 block">
              Supports markdown: **bold**, *italic*, [link](url)
            </span>
          </div>

          {/* Link URL */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Link URL (required)
            </label>
            <input
              type="url"
              value={block.props?.linkUrl || ""}
              onChange={(e) => onUpdate("linkUrl", e.target.value)}
              onBlur={() => {
                setTouched((t) => ({ ...t, linkUrl: true }));
                onUpdate(
                  "linkUrl",
                  normalizeWebUrl(block.props?.linkUrl || ""),
                );
              }}
              className={`form-control${touched.linkUrl && urlInvalid(block.props?.linkUrl) ? " error" : ""}`}
              placeholder="https://example.com/deal or example.com/deal"
            />
          </div>

          {/* Link Text */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-neutral-700 mb-2">
              Button Text
            </label>
            <input
              type="text"
              value={block.props?.linkText || "View Deal"}
              onChange={(e) => onUpdate("linkText", e.target.value)}
              maxLength={50}
              className="form-control"
              placeholder="View Deal"
            />
          </div>

          {/* Badge Settings */}
          <div className="grid grid-cols-2 gap-4">
            {/* Badge Text */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Badge Text (required)
              </label>
              <input
                type="text"
                value={block.props?.badgeText || ""}
                onChange={(e) => onUpdate("badgeText", e.target.value)}
                onBlur={() => setTouched((t) => ({ ...t, badgeText: true }))}
                maxLength={20}
                className={`form-control${touched.badgeText && isBlank(block.props?.badgeText) ? " error" : ""}`}
                placeholder="70% OFF"
              />
            </div>

            {/* Badge Color */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Badge Color
              </label>
              <select
                value={block.props?.badgeColor || "green"}
                onChange={(e) => onUpdate("badgeColor", e.target.value)}
                className="form-control"
              >
                <option value="green">Green</option>
                <option value="yellow">Yellow</option>
                <option value="orange">Orange</option>
                <option value="red">Red</option>
                <option value="purple">Purple</option>
                <option value="darkblue">Dark Blue</option>
                <option value="gray">Gray</option>
              </select>
            </div>
          </div>

          {/* Badge Preview */}
          {block.props?.badgeText && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-neutral-700 mb-2">
                Badge Preview
              </label>
              <div className="inline-flex">
                <span
                  className={`
              inline-flex items-center justify-center font-semibold text-sm
              rounded-full px-3 py-1 text-white
              ${block.props?.badgeColor === "green" ? "bg-green-500" : ""}
              ${block.props?.badgeColor === "yellow" ? "bg-yellow-500 text-neutral-900" : ""}
              ${block.props?.badgeColor === "orange" ? "bg-orange-500" : ""}
              ${block.props?.badgeColor === "red" ? "bg-red-500" : ""}
              ${block.props?.badgeColor === "purple" ? "bg-purple-500" : ""}
              ${block.props?.badgeColor === "darkblue" ? "bg-blue-900" : ""}
              ${block.props?.badgeColor === "gray" ? "bg-gray-500" : ""}
            `}
                >
                  {block.props.badgeText}
                </span>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * View Mode Component
 *
 * Renders the discount card in its final display format
 * matching the server-side rendered version
 */
function DiscountCardViewer({ block }) {
  const props = block.props || {};
  const {
    logoImageUrl,
    title = "",
    description = "",
    details = "",
    linkUrl = "#",
    linkText = "View Deal",
    badgeText = "",
    badgeColor = "green",
  } = props;

  // Sanitize HTML content and URLs to prevent XSS
  const sanitizedDescription = useMemo(
    () => sanitizeHtml(description),
    [description],
  );
  const sanitizedDetails = useMemo(() => sanitizeHtml(details), [details]);
  const sanitizedLinkUrl = useMemo(() => sanitizeUrl(linkUrl), [linkUrl]);

  // Get badge color classes
  const getBadgeClasses = () => {
    const baseClasses =
      "inline-flex items-center justify-center font-semibold text-sm rounded-full px-4 py-2";
    switch (badgeColor) {
      case "green":
        return `${baseClasses} bg-green-500 text-white`;
      case "yellow":
        return `${baseClasses} bg-yellow-500 text-neutral-900`;
      case "orange":
        return `${baseClasses} bg-orange-500 text-white`;
      case "red":
        return `${baseClasses} bg-red-500 text-white`;
      case "purple":
        return `${baseClasses} bg-purple-500 text-white`;
      case "darkblue":
        return `${baseClasses} bg-blue-900 text-white`;
      case "gray":
        return `${baseClasses} bg-gray-500 text-white`;
      default:
        return `${baseClasses} bg-green-500 text-white`;
    }
  };

  return (
    <div className="discount-card my-6">
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="md:flex">
          {/* Badge Column (Desktop Left, Mobile Top) */}
          <div className="md:w-1/4 bg-gray-50 flex items-center justify-center p-6">
            <div className={getBadgeClasses()}>
              <span className="text-2xl font-bold">{badgeText}</span>
            </div>
          </div>

          {/* Content Column (Desktop Right, Mobile Bottom) */}
          <div className="md:w-3/4 p-6">
            {/* Company Logo and Title */}
            <div className="flex flex-col items-center mb-4">
              {logoImageUrl && (
                <img
                  src={logoImageUrl}
                  alt={`${title} logo`}
                  className="w-auto h-[200px] object-contain mr-4"
                />
              )}
              <div>
                <h3 className="text-xl font-bold text-neutral-900">{title}</h3>
              </div>
            </div>

            {/* Description */}
            {sanitizedDescription && (
              <div
                className="text-neutral-700 mb-4 prose prose-sm"
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              />
            )}

            {/* Details Section */}
            {sanitizedDetails && (
              <div className="border border-gray-200 rounded-md p-4 mb-4 bg-gray-50">
                <div
                  className="prose prose-sm text-neutral-700"
                  dangerouslySetInnerHTML={{ __html: sanitizedDetails }}
                />
              </div>
            )}

            {/* CTA Button */}
            <a
              href={sanitizedLinkUrl}
              target="_blank"
              rel="noopener"
              className="inline-block px-6 py-3 bg-red-600 text-white font-semibold rounded-md hover:bg-red-700 transition-colors"
            >
              {linkText}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Main DiscountCardBlock Component
 *
 * Handles mode detection and delegates rendering to appropriate sub-component
 */
export default function DiscountCardBlock({ block, editor }) {
  const [isEditMode, setIsEditMode] = useState(true);

  // Detect edit/view mode
  useEffect(() => {
    setIsEditMode(editor?.isEditable ?? true);
  }, [editor]);

  // Update block property
  const updateProp = useCallback(
    (key, value) => {
      if (!editor) return;

      // Use editor.getBlock to get the current block state
      // This ensures we have the latest block reference
      const currentBlock = editor.getBlock(block.id);
      if (!currentBlock) {
        console.error("Block not found:", block.id);
        return;
      }

      editor.updateBlock(currentBlock, {
        props: { ...currentBlock.props, [key]: value },
      });
    },
    [editor, block.id],
  );

  // Initialize default props if needed
  useEffect(() => {
    if (!block.props) {
      editor?.updateBlock(block, {
        props: {
          title: "",
          description: "",
          details: "",
          linkUrl: "",
          linkText: "View Deal",
          badgeText: "",
          badgeColor: "green",
          logoImageUrl: "",
        },
      });
    }
  }, [block, editor]);

  if (isEditMode) {
    return <DiscountCardEditor block={block} onUpdate={updateProp} />;
  }

  return <DiscountCardViewer block={block} />;
}
